import { API_URL } from '@/config';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  Image, Save, Eye, RefreshCw, Upload, Plus, Trash2, Move, Type, 
  Link2, Copy, ExternalLink, Globe, MessageCircle, Facebook, Instagram, 
  Twitter, Youtube, GripVertical, Settings, Layers, X, Check, QrCode, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import axios from 'axios';
import { toast } from 'sonner';
import getImageUrl from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const DynamicFlyerDesigner = () => {
  const { user } = useOutletContext();
  const [flyers, setFlyers] = useState([]);
  const [selectedFlyer, setSelectedFlyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draggedPlaceholder, setDraggedPlaceholder] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [showNewFlyerDialog, setShowNewFlyerDialog] = useState(false);
  const [newFlyerName, setNewFlyerName] = useState('');
  const [showQRDialog, setShowQRDialog] = useState(false);
  
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const qrCodeRef = useRef(null);
  
  // Default flyer template
  const defaultFlyer = {
    name: '',
    description: '',
    base_image: '',
    canvas_width: 800,
    canvas_height: 1000,
    placeholders: [],
    social_links: {
      website: '',
      whatsapp: '',
      facebook: '',
      instagram: '',
      twitter: '',
      youtube: ''
    },
    show_social_on_flyer: true,
    show_social_buttons: true,
    social_position: { x: 50, y: 92 },
    background_color: '#FFFFFF',
    is_active: true
  };

  useEffect(() => {
    loadFlyers();
  }, []);

  const loadFlyers = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/flyers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlyers(response.data);
      if (response.data.length > 0 && !selectedFlyer) {
        setSelectedFlyer(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to load flyers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFlyer = async () => {
    if (!newFlyerName.trim()) {
      toast.error('Please enter a flyer name');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/flyers`, {
        ...defaultFlyer,
        name: newFlyerName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFlyers(prev => [response.data, ...prev]);
      setSelectedFlyer(response.data);
      setShowNewFlyerDialog(false);
      setNewFlyerName('');
      toast.success('Flyer template created!');
    } catch (error) {
      toast.error('Failed to create flyer');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFlyer = async () => {
    if (!selectedFlyer) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const { flyer_id, ...updateData } = selectedFlyer;
      
      if (flyer_id) {
        await axios.put(`${API}/flyers/${flyer_id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success('Flyer saved successfully!');
      loadFlyers();
    } catch (error) {
      toast.error('Failed to save flyer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFlyer = async (flyerId) => {
    if (!window.confirm('Are you sure you want to delete this flyer?')) return;
    
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/flyers/${flyerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFlyers(prev => prev.filter(f => f.flyer_id !== flyerId));
      if (selectedFlyer?.flyer_id === flyerId) {
        setSelectedFlyer(flyers.find(f => f.flyer_id !== flyerId) || null);
      }
      toast.success('Flyer deleted');
    } catch (error) {
      toast.error('Failed to delete flyer');
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSelectedFlyer(prev => ({
        ...prev,
        base_image: response.data.url
      }));
      toast.success('Base image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Add placeholder
  const addPlaceholder = (type) => {
    const newPlaceholder = {
      id: `ph_${Date.now()}`,
      type, // 'image' or 'text'
      label: type === 'image' ? 'Photo' : 'Text',
      x: 50, // percentage
      y: 50,
      width: type === 'image' ? 20 : 40,
      height: type === 'image' ? 20 : 10,
      fontSize: 24,
      fontColor: '#000000',
      fontFamily: 'Arial',
      textAlign: 'center',
      backgroundColor: type === 'image' ? 'transparent' : 'transparent',
      borderRadius: type === 'image' ? 50 : 0, // Circle for images by default
      placeholder_text: type === 'text' ? 'Your text here' : '',
      required: true
    };
    
    setSelectedFlyer(prev => ({
      ...prev,
      placeholders: [...(prev.placeholders || []), newPlaceholder]
    }));
  };

  // Update placeholder
  const updatePlaceholder = (id, updates) => {
    setSelectedFlyer(prev => ({
      ...prev,
      placeholders: prev.placeholders.map(p => 
        p.id === id ? { ...p, ...updates } : p
      )
    }));
  };

  // Remove placeholder
  const removePlaceholder = (id) => {
    setSelectedFlyer(prev => ({
      ...prev,
      placeholders: prev.placeholders.filter(p => p.id !== id)
    }));
  };

  // Handle drag on canvas
  const handleCanvasMouseDown = (e, placeholder) => {
    if (isResizing) return;
    setDraggedPlaceholder(placeholder.id);
  };

  const handleCanvasMouseMove = useCallback((e) => {
    if (!draggedPlaceholder || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    updatePlaceholder(draggedPlaceholder, {
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    });
  }, [draggedPlaceholder]);

  const handleCanvasMouseUp = () => {
    setDraggedPlaceholder(null);
  };

  useEffect(() => {
    if (draggedPlaceholder) {
      window.addEventListener('mousemove', handleCanvasMouseMove);
      window.addEventListener('mouseup', handleCanvasMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleCanvasMouseMove);
        window.removeEventListener('mouseup', handleCanvasMouseUp);
      };
    }
  }, [draggedPlaceholder, handleCanvasMouseMove]);

  const updateSocialLink = (key, value) => {
    setSelectedFlyer(prev => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [key]: value
      }
    }));
  };

  const copyPublicLink = () => {
    if (!selectedFlyer?.flyer_id) return;
    const link = `${window.location.origin}/flyer/${selectedFlyer.flyer_id}`;
    navigator.clipboard.writeText(link);
    toast.success('Public link copied to clipboard!');
  };

  const getPublicLink = () => {
    if (!selectedFlyer?.flyer_id) return '';
    return `${window.location.origin}/flyer/${selectedFlyer.flyer_id}`;
  };

  const downloadQRCode = (format = 'png') => {
    if (!qrCodeRef.current) return;
    
    const canvas = qrCodeRef.current.querySelector('canvas');
    if (!canvas) return;
    
    const link = document.createElement('a');
    link.download = `${selectedFlyer?.name || 'flyer'}-qr-code.${format}`;
    link.href = canvas.toDataURL(`image/${format}`);
    link.click();
    toast.success('QR code downloaded!');
  };

  const socialIcons = {
    website: Globe,
    whatsapp: MessageCircle,
    facebook: Facebook,
    instagram: Instagram,
    twitter: Twitter,
    youtube: Youtube
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dynamic-flyer-designer">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Dynamic Flyer Designer</h1>
          <p className="text-sm text-muted-foreground">Create customizable flyer templates with drag-and-drop placeholders</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowNewFlyerDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Flyer
          </Button>
          {selectedFlyer && (
            <>
              <Button variant="outline" onClick={() => setShowPreview(true)}>
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button onClick={handleSaveFlyer} disabled={saving} style={{ backgroundColor: '#D4AF37' }}>
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Flyer List */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Flyer Templates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
              {flyers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No flyers yet. Create one!
                </p>
              ) : (
                flyers.map((flyer) => (
                  <div
                    key={flyer.flyer_id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedFlyer?.flyer_id === flyer.flyer_id
                        ? 'border-amber-500 bg-amber-50'
                        : 'hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedFlyer(flyer)}
                    data-testid={`flyer-item-${flyer.flyer_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{flyer.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {flyer.placeholders?.length || 0} placeholders
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge variant={flyer.is_active ? 'default' : 'secondary'} className="text-xs">
                          {flyer.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500"
                          onClick={(e) => { e.stopPropagation(); handleDeleteFlyer(flyer.flyer_id); }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Canvas & Settings */}
        {selectedFlyer ? (
          <>
            {/* Canvas Area */}
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Design Canvas</CardTitle>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => addPlaceholder('image')}>
                        <Image className="w-4 h-4 mr-1" />
                        Add Photo
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => addPlaceholder('text')}>
                        <Type className="w-4 h-4 mr-1" />
                        Add Text
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Base Image Upload */}
                  {!selectedFlyer.base_image ? (
                    <div
                      className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-amber-500 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files?.[0])}
                      />
                      <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                      <p className="font-medium">Upload Base Flyer Image</p>
                      <p className="text-sm text-muted-foreground">Click to upload your flyer design</p>
                    </div>
                  ) : (
                    <div
                      ref={canvasRef}
                      className="relative rounded-lg overflow-hidden mx-auto"
                      style={{
                        maxWidth: '100%',
                        aspectRatio: `${selectedFlyer.canvas_width} / ${selectedFlyer.canvas_height}`,
                        backgroundColor: selectedFlyer.background_color
                      }}
                    >
                      <img
                        src={getImageUrl(selectedFlyer.base_image)}
                        alt="Base flyer"
                        className="w-full h-full object-contain"
                        draggable={false}
                      />
                      
                      {/* Placeholders */}
                      {selectedFlyer.placeholders?.map((ph) => (
                        <div
                          key={ph.id}
                          className={`absolute cursor-move border-2 border-dashed flex items-center justify-center transition-all ${
                            draggedPlaceholder === ph.id ? 'border-amber-500 z-20' : 'border-blue-400 hover:border-blue-600'
                          }`}
                          style={{
                            left: `${ph.x - ph.width / 2}%`,
                            top: `${ph.y - ph.height / 2}%`,
                            width: `${ph.width}%`,
                            height: `${ph.height}%`,
                            borderRadius: ph.type === 'image' ? `${ph.borderRadius}%` : `${ph.borderRadius}px`,
                            backgroundColor: ph.backgroundColor !== 'transparent' ? ph.backgroundColor : 'rgba(59, 130, 246, 0.1)',
                            fontSize: `${ph.fontSize * 0.5}px`,
                            color: ph.fontColor,
                            fontFamily: ph.fontFamily
                          }}
                          onMouseDown={(e) => handleCanvasMouseDown(e, ph)}
                          data-testid={`placeholder-${ph.id}`}
                        >
                          <div className="text-center p-1 overflow-hidden">
                            {ph.type === 'image' ? (
                              <div className="flex flex-col items-center">
                                <Image className="w-6 h-6 text-blue-500" />
                                <span className="text-xs text-blue-600 mt-1">{ph.label}</span>
                              </div>
                            ) : (
                              <span className="text-xs">{ph.label}</span>
                            )}
                          </div>
                          
                          {/* Delete button */}
                          <button
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 z-30"
                            onClick={(e) => { e.stopPropagation(); removePlaceholder(ph.id); }}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      
                      {/* Social Links Preview on Flyer */}
                      {selectedFlyer.show_social_on_flyer && (
                        <div
                          className="absolute flex gap-2 items-center"
                          style={{
                            left: `${selectedFlyer.social_position?.x || 50}%`,
                            top: `${selectedFlyer.social_position?.y || 92}%`,
                            transform: 'translate(-50%, -50%)'
                          }}
                        >
                          {Object.entries(selectedFlyer.social_links || {}).map(([key, value]) => {
                            if (!value) return null;
                            const Icon = socialIcons[key];
                            return (
                              <div key={key} className="w-6 h-6 rounded-full bg-gray-800 flex items-center justify-center">
                                <Icon className="w-3 h-3 text-white" />
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Change image button */}
                      <Button
                        variant="secondary"
                        size="sm"
                        className="absolute bottom-2 right-2"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Change
                      </Button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files?.[0])}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Public Link & QR Code */}
              {selectedFlyer.flyer_id && (
                <Card>
                  <CardContent className="pt-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <Input
                        readOnly
                        value={`${window.location.origin}/flyer/${selectedFlyer.flyer_id}`}
                        className="flex-1 text-sm"
                      />
                      <Button variant="outline" size="sm" onClick={copyPublicLink}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => window.open(`/flyer/${selectedFlyer.flyer_id}`, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* QR Code Section */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-lg border shadow-sm">
                          <QRCodeSVG 
                            value={getPublicLink()} 
                            size={64}
                            level="M"
                            includeMargin={false}
                          />
                        </div>
                        <div>
                          <p className="font-medium text-sm">QR Code</p>
                          <p className="text-xs text-muted-foreground">Scan to open flyer page</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowQRDialog(true)}
                        data-testid="qr-code-btn"
                      >
                        <QrCode className="w-4 h-4 mr-1" />
                        View & Download
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Settings Panel */}
            <div className="space-y-4">
              <Tabs defaultValue="settings" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                  <TabsTrigger value="placeholders">Placeholders</TabsTrigger>
                  <TabsTrigger value="social">Social</TabsTrigger>
                </TabsList>

                {/* Settings Tab */}
                <TabsContent value="settings" className="space-y-4">
                  <Card>
                    <CardContent className="pt-4 space-y-4">
                      <div>
                        <Label>Flyer Name</Label>
                        <Input
                          value={selectedFlyer.name}
                          onChange={(e) => setSelectedFlyer(prev => ({ ...prev, name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={selectedFlyer.description}
                          onChange={(e) => setSelectedFlyer(prev => ({ ...prev, description: e.target.value }))}
                          rows={2}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Canvas Width</Label>
                          <Input
                            type="number"
                            value={selectedFlyer.canvas_width}
                            onChange={(e) => setSelectedFlyer(prev => ({ ...prev, canvas_width: parseInt(e.target.value) || 800 }))}
                          />
                        </div>
                        <div>
                          <Label>Canvas Height</Label>
                          <Input
                            type="number"
                            value={selectedFlyer.canvas_height}
                            onChange={(e) => setSelectedFlyer(prev => ({ ...prev, canvas_height: parseInt(e.target.value) || 1000 }))}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Background Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={selectedFlyer.background_color}
                            onChange={(e) => setSelectedFlyer(prev => ({ ...prev, background_color: e.target.value }))}
                            className="w-12 h-10 p-1 cursor-pointer"
                          />
                          <Input
                            value={selectedFlyer.background_color}
                            onChange={(e) => setSelectedFlyer(prev => ({ ...prev, background_color: e.target.value }))}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Active</Label>
                        <Switch
                          checked={selectedFlyer.is_active}
                          onCheckedChange={(checked) => setSelectedFlyer(prev => ({ ...prev, is_active: checked }))}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Placeholders Tab */}
                <TabsContent value="placeholders" className="space-y-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Placeholder Settings</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 max-h-[400px] overflow-y-auto">
                      {selectedFlyer.placeholders?.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No placeholders. Add photo or text placeholders from the canvas.
                        </p>
                      ) : (
                        selectedFlyer.placeholders?.map((ph, index) => (
                          <div key={ph.id} className="p-3 border rounded-lg space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {ph.type === 'image' ? (
                                  <Image className="w-4 h-4 text-blue-500" />
                                ) : (
                                  <Type className="w-4 h-4 text-green-500" />
                                )}
                                <span className="font-medium text-sm">
                                  {ph.type === 'image' ? 'Photo' : 'Text'} #{index + 1}
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-500"
                                onClick={() => removePlaceholder(ph.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                            
                            <div>
                              <Label className="text-xs">Label</Label>
                              <Input
                                value={ph.label}
                                onChange={(e) => updatePlaceholder(ph.id, { label: e.target.value })}
                                className="h-8 text-sm"
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Width (%)</Label>
                                <Slider
                                  value={[ph.width]}
                                  onValueChange={([val]) => updatePlaceholder(ph.id, { width: val })}
                                  min={5}
                                  max={80}
                                  step={1}
                                  className="mt-2"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Height (%)</Label>
                                <Slider
                                  value={[ph.height]}
                                  onValueChange={([val]) => updatePlaceholder(ph.id, { height: val })}
                                  min={5}
                                  max={80}
                                  step={1}
                                  className="mt-2"
                                />
                              </div>
                            </div>
                            
                            {ph.type === 'text' && (
                              <>
                                <div>
                                  <Label className="text-xs">Font Size</Label>
                                  <Slider
                                    value={[ph.fontSize]}
                                    onValueChange={([val]) => updatePlaceholder(ph.id, { fontSize: val })}
                                    min={12}
                                    max={72}
                                    step={1}
                                    className="mt-2"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Font Color</Label>
                                  <div className="flex gap-2">
                                    <Input
                                      type="color"
                                      value={ph.fontColor}
                                      onChange={(e) => updatePlaceholder(ph.id, { fontColor: e.target.value })}
                                      className="w-10 h-8 p-1"
                                    />
                                    <Input
                                      value={ph.fontColor}
                                      onChange={(e) => updatePlaceholder(ph.id, { fontColor: e.target.value })}
                                      className="flex-1 h-8 text-sm"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-xs">Placeholder Text</Label>
                                  <Input
                                    value={ph.placeholder_text}
                                    onChange={(e) => updatePlaceholder(ph.id, { placeholder_text: e.target.value })}
                                    className="h-8 text-sm"
                                    placeholder="Enter placeholder text..."
                                  />
                                </div>
                              </>
                            )}
                            
                            {ph.type === 'image' && (
                              <div>
                                <Label className="text-xs">Border Radius (%)</Label>
                                <Slider
                                  value={[ph.borderRadius]}
                                  onValueChange={([val]) => updatePlaceholder(ph.id, { borderRadius: val })}
                                  min={0}
                                  max={50}
                                  step={1}
                                  className="mt-2"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                  {ph.borderRadius === 50 ? 'Circle' : ph.borderRadius === 0 ? 'Square' : 'Rounded'}
                                </p>
                              </div>
                            )}
                            
                            <div className="flex items-center justify-between">
                              <Label className="text-xs">Required</Label>
                              <Switch
                                checked={ph.required}
                                onCheckedChange={(checked) => updatePlaceholder(ph.id, { required: checked })}
                              />
                            </div>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Social Tab */}
                <TabsContent value="social" className="space-y-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Social Links</CardTitle>
                      <CardDescription className="text-xs">
                        Add clickable links that appear on the flyer
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {Object.entries(socialIcons).map(([key, Icon]) => (
                        <div key={key} className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-gray-500" />
                          <Input
                            value={selectedFlyer.social_links?.[key] || ''}
                            onChange={(e) => updateSocialLink(key, e.target.value)}
                            placeholder={`${key.charAt(0).toUpperCase() + key.slice(1)} URL`}
                            className="h-8 text-sm"
                          />
                        </div>
                      ))}
                      
                      <div className="pt-3 space-y-3 border-t">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Show on Flyer</Label>
                          <Switch
                            checked={selectedFlyer.show_social_on_flyer}
                            onCheckedChange={(checked) => setSelectedFlyer(prev => ({ ...prev, show_social_on_flyer: checked }))}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Show as Buttons</Label>
                          <Switch
                            checked={selectedFlyer.show_social_buttons}
                            onCheckedChange={(checked) => setSelectedFlyer(prev => ({ ...prev, show_social_buttons: checked }))}
                          />
                        </div>
                        
                        {selectedFlyer.show_social_on_flyer && (
                          <div>
                            <Label className="text-xs">Position on Flyer</Label>
                            <div className="grid grid-cols-2 gap-2 mt-2">
                              <div>
                                <Label className="text-xs text-muted-foreground">X (%)</Label>
                                <Slider
                                  value={[selectedFlyer.social_position?.x || 50]}
                                  onValueChange={([val]) => setSelectedFlyer(prev => ({
                                    ...prev,
                                    social_position: { ...prev.social_position, x: val }
                                  }))}
                                  min={0}
                                  max={100}
                                  step={1}
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Y (%)</Label>
                                <Slider
                                  value={[selectedFlyer.social_position?.y || 92]}
                                  onValueChange={([val]) => setSelectedFlyer(prev => ({
                                    ...prev,
                                    social_position: { ...prev.social_position, y: val }
                                  }))}
                                  min={0}
                                  max={100}
                                  step={1}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </>
        ) : (
          <div className="lg:col-span-3 flex items-center justify-center py-12">
            <div className="text-center">
              <Layers className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-muted-foreground">Select a flyer or create a new one</p>
            </div>
          </div>
        )}
      </div>

      {/* New Flyer Dialog */}
      <Dialog open={showNewFlyerDialog} onOpenChange={setShowNewFlyerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Flyer</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Flyer Name</Label>
            <Input
              value={newFlyerName}
              onChange={(e) => setNewFlyerName(e.target.value)}
              placeholder="Enter flyer name..."
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewFlyerDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateFlyer} disabled={saving} style={{ backgroundColor: '#D4AF37' }}>
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Flyer Preview (User View)</DialogTitle>
          </DialogHeader>
          <div className="p-4 bg-gray-100 rounded-lg">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-md mx-auto">
              {/* Flyer Header */}
              <div className="p-4 text-center border-b">
                <h2 className="text-xl font-bold">{selectedFlyer?.name}</h2>
                {selectedFlyer?.description && (
                  <p className="text-sm text-muted-foreground mt-1">{selectedFlyer.description}</p>
                )}
              </div>
              
              {/* Flyer Canvas Preview */}
              <div
                className="relative mx-auto"
                style={{
                  aspectRatio: `${selectedFlyer?.canvas_width || 800} / ${selectedFlyer?.canvas_height || 1000}`,
                  backgroundColor: selectedFlyer?.background_color || '#FFFFFF'
                }}
              >
                {selectedFlyer?.base_image && (
                  <img
                    src={getImageUrl(selectedFlyer.base_image)}
                    alt="Flyer"
                    className="w-full h-full object-contain"
                  />
                )}
                
                {/* Placeholder indicators */}
                {selectedFlyer?.placeholders?.map((ph) => (
                  <div
                    key={ph.id}
                    className="absolute border-2 border-dashed border-blue-300 bg-blue-50/50 flex items-center justify-center"
                    style={{
                      left: `${ph.x - ph.width / 2}%`,
                      top: `${ph.y - ph.height / 2}%`,
                      width: `${ph.width}%`,
                      height: `${ph.height}%`,
                      borderRadius: ph.type === 'image' ? `${ph.borderRadius}%` : `${ph.borderRadius}px`
                    }}
                  >
                    <span className="text-xs text-blue-600 text-center px-1">
                      {ph.type === 'image' ? '📷' : '✏️'} {ph.label}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* Social Buttons */}
              {selectedFlyer?.show_social_buttons && (
                <div className="p-4 border-t flex flex-wrap justify-center gap-2">
                  {Object.entries(selectedFlyer.social_links || {}).map(([key, value]) => {
                    if (!value) return null;
                    const Icon = socialIcons[key];
                    return (
                      <Button key={key} variant="outline" size="sm" className="gap-2">
                        <Icon className="w-4 h-4" />
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              QR Code for {selectedFlyer?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-6 space-y-4">
            <div 
              ref={qrCodeRef}
              className="p-6 bg-white rounded-xl border-2 shadow-lg"
            >
              <QRCodeCanvas 
                value={getPublicLink()} 
                size={200}
                level="H"
                includeMargin={true}
                bgColor="#FFFFFF"
                fgColor="#0F172A"
              />
            </div>
            <p className="text-sm text-muted-foreground text-center max-w-xs">
              Scan this QR code to open the public flyer page where users can create their personalized flyers.
            </p>
            <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg w-full">
              <Input
                readOnly
                value={getPublicLink()}
                className="text-xs border-0 bg-transparent"
              />
              <Button variant="ghost" size="sm" onClick={copyPublicLink}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => downloadQRCode('png')}
              className="w-full sm:w-auto"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PNG
            </Button>
            <Button 
              onClick={() => downloadQRCode('jpeg')}
              className="w-full sm:w-auto"
              style={{ backgroundColor: '#D4AF37' }}
            >
              <Download className="w-4 h-4 mr-2" />
              Download JPEG
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DynamicFlyerDesigner;
